/**
 * `SimulationControls` — the sticky control strip for the Live Agent
 * view (ADR-0163 §4, amended 2026-07-05): agent picker + Run/Pause.
 * Reset relocated to the phase-list / endpoint-column headers beside
 * the ViewToggle (`ResetIconButton`) — it's a rare action and doesn't
 * earn permanent sticky space.
 *
 * Sticky posture: `position: sticky; top: 0; z-index: 20` — strictly
 * BELOW the `AgentDropdown` open panel (`z-30`) so the panel never
 * renders under the bar. That ordering is the load-bearing z-index
 * invariant (test-pinned by reading both values from the DOM).
 *
 * Layout: ONE row on both viewports. `<md`: [dropdown flex-1][Run
 * fixed-116]. `md+`: [AGENT kicker][dropdown][Run fixed-116]. The
 * `ViewToggle` + `ResetIconButton` live in the EndpointColumn header
 * (desktop) / UnifiedPhaseList header (mobile) — one locus per
 * viewport, same visual role.
 */

import { Pause, Play, RotateCcw } from 'lucide-react';

import { useC } from '../../../lib/theme.js';
import { useIsMobile } from '../../../lib/use-is-mobile.js';
import type { AgentDef, VerificationTier } from '../roster.js';
import { AgentDropdown } from './AgentDropdown.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface SimulationControlsProps {
  agent: AgentDef;
  agentId: string;
  onChangeAgent: (id: string) => void;
  dropdownDisabled: boolean;
  running: boolean;
  step: number;
  phaseCount: number;
  onRun: () => void;
  onPause: () => void;
  tierFor: (rosterId: string) => VerificationTier;
}

function RunButton({
  running,
  isComplete,
  hasStarted,
  isBlocked,
  onRun,
  onPause,
}: {
  running: boolean;
  isComplete: boolean;
  hasStarted: boolean;
  isBlocked: boolean;
  onRun: () => void;
  onPause: () => void;
}): JSX.Element {
  const C = useC();
  return (
    <button
      onClick={running ? onPause : onRun}
      className="flex items-center justify-center gap-1.5 flex-shrink-0"
      style={{
        height: 44,
        // FIXED width sized to the widest label ("Run again"). The
        // label changes mid-session (Run → Pause → Run again); a
        // variable-width button makes the adjacent dropdown twitch on
        // every state change.
        width: 116,
        background: isBlocked ? C.coral : C.lime,
        color: isBlocked ? '#FFFFFF' : C.ink,
        fontFamily: sansFont,
        fontWeight: 600,
        fontSize: 12,
        borderRadius: 2,
      }}
    >
      {running ? (
        <>
          <Pause size={11} /> Pause
        </>
      ) : isComplete ? (
        <>
          <RotateCcw size={11} /> Run again
        </>
      ) : hasStarted ? (
        <>
          <Play size={11} /> Resume
        </>
      ) : (
        <>
          <Play size={11} /> Run
        </>
      )}
    </button>
  );
}

export function SimulationControls({
  agent,
  agentId,
  onChangeAgent,
  dropdownDisabled,
  running,
  step,
  phaseCount,
  onRun,
  onPause,
  tierFor,
}: SimulationControlsProps): JSX.Element {
  const C = useC();
  const isMobile = useIsMobile();
  const isComplete = step >= phaseCount - 1 && step >= 0;
  const isBlocked = agent.protocol === 'blocked';

  return (
    // Outer wrapper carries the sticky posture + the PAGE background so
    // scrolled content disappears cleanly behind it; the inner box is a
    // Card-styled frame so the controls (and the Run button's right
    // edge) align with every other card on the page instead of
    // bleeding to the raw content edge.
    <div
      data-testid="simulation-controls"
      className="pb-5"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        background: C.bg,
      }}
    >
      <div
        className="px-4 py-3"
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
        }}
      >
      {isMobile ? (
        // Single row — the founder-locked mobile shape (2026-07-05):
        // picker + Run, nothing else. The AGENT kicker is dropped (the
        // trigger's icon tile + name carry the meaning) and Reset
        // relocates to the phase-list header beside the ViewToggle.
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <AgentDropdown agentId={agentId} onChange={onChangeAgent} disabled={dropdownDisabled} tierFor={tierFor} />
          </div>
          <RunButton running={running} isComplete={isComplete} hasStarted={step >= 0} isBlocked={isBlocked} onRun={onRun} onPause={onPause} />
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 2, flexShrink: 0 }}>AGENT</span>
          <div className="flex-1 min-w-[180px]">
            <AgentDropdown agentId={agentId} onChange={onChangeAgent} disabled={dropdownDisabled} tierFor={tierFor} />
          </div>
          <RunButton running={running} isComplete={isComplete} hasStarted={step >= 0} isBlocked={isBlocked} onRun={onRun} onPause={onPause} />
        </div>
      )}
      </div>
    </div>
  );
}
